import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Search,
  MoreHorizontal,
  Truck,
  Loader2,
  Eye,
  CheckCircle,
  Clock,
  Package,
  Send,
  MapPin,
  RotateCcw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { useSessionState } from '@/hooks/use-session-state';

const statusConfig: Record<string, { color: string; icon: any }> = {
  PENDING: { color: 'bg-gray-100 text-gray-700', icon: Clock },
  PICKED: { color: 'bg-amber-100 text-amber-700', icon: Package },
  PACKED: { color: 'bg-blue-100 text-blue-700', icon: Package },
  SHIPPED: { color: 'bg-purple-100 text-purple-700', icon: Send },
  IN_TRANSIT: { color: 'bg-cyan-100 text-cyan-700', icon: Truck },
  DELIVERED: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
  RETURNED: { color: 'bg-red-100 text-red-700', icon: RotateCcw },
};

export function DeliveriesPage() {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const soIdFromUrl = searchParams.get('soId');

  const [debouncedSearch, setDebouncedSearch] = useSessionState(
    'trading:sales-deliveries:debouncedSearch',
    ''
  );
  const [searchQuery, setSearchQuery] = useState(debouncedSearch);
  const [selectedStatus, setSelectedStatus] = useSessionState<string>(
    'trading:sales-deliveries:selectedStatus',
    'all'
  );
  const [page, setPage] = useState(1);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  };

  const { data, isLoading, error } = trpc.tradingDelivery.list.useQuery({
    search: debouncedSearch || undefined,
    status:
      selectedStatus !== 'all'
        ? (selectedStatus as
            | 'PENDING'
            | 'PICKED'
            | 'PACKED'
            | 'SHIPPED'
            | 'IN_TRANSIT'
            | 'DELIVERED'
            | 'RETURNED')
        : undefined,
    salesOrderId: soIdFromUrl || undefined,
    page,
  });

  const utils = trpc.useUtils();

  const markShippedMutation = trpc.tradingDelivery.markShipped.useMutation({
    onSuccess: () => {
      utils.tradingDelivery.list.invalidate();
      showSuccessToast(t('common.updated'));
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const markDeliveredMutation = trpc.tradingDelivery.markDelivered.useMutation({
    onSuccess: () => {
      utils.tradingDelivery.list.invalidate();
      showSuccessToast(t('common.updated'));
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const deliveries = data?.deliveries ?? [];
  const pagination = data?.pagination;

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING':
        return t('sales.deliveries.pending');
      case 'PICKED':
        return t('sales.deliveries.picked');
      case 'PACKED':
        return t('sales.deliveries.packed');
      case 'SHIPPED':
        return t('sales.deliveries.shipped');
      case 'IN_TRANSIT':
        return t('sales.deliveries.inTransit');
      case 'DELIVERED':
        return t('sales.deliveries.delivered');
      case 'RETURNED':
        return t('sales.deliveries.returned');
      default:
        return status;
    }
  };

  const formatDate = (dateValue: string | Date | null | undefined) => {
    if (!dateValue) return '-';
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const statuses = [
    'PENDING',
    'PICKED',
    'PACKED',
    'SHIPPED',
    'IN_TRANSIT',
    'DELIVERED',
    'RETURNED',
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('sales.deliveries.title')}</h1>
          <p className="text-muted-foreground">{t('sales.deliveries.subtitle')}</p>
        </div>
        <Button onClick={() => navigate('/trading/sales/deliveries/new')}>
          <Plus className="mr-2 h-4 w-4" />
          {t('sales.deliveries.addDelivery')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <input
            type="text"
            placeholder={t('sales.deliveries.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 w-full border pl-10 pr-4 text-sm focus:outline-none focus:ring-1"
          />
        </div>
        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('sales.deliveries.selectStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('sales.deliveries.allStatuses')}</SelectItem>
            {statuses.map((status) => (
              <SelectItem key={status} value={status}>
                {getStatusLabel(status)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
          <span className="text-muted-foreground ml-2">{t('common.loading')}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('common.error')}</p>
          <p className="text-sm">{error.message}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && deliveries.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Truck className="text-muted-foreground/50 h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">{t('sales.deliveries.noDeliveries')}</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {t('sales.deliveries.noDeliveriesDesc')}
          </p>
        </div>
      )}

      {/* Deliveries Table */}
      {!isLoading && !error && deliveries.length > 0 && (
        <>
          <div className="border-border bg-card overflow-hidden border">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('sales.deliveries.deliveryNumber')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('sales.deliveries.salesOrder')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('sales.deliveries.customer')}
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium">
                    {t('sales.deliveries.status')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('sales.deliveries.warehouse')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('sales.deliveries.trackingNumber')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('sales.deliveries.shipDate')}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium">
                    {t('sales.deliveries.items')}
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium">
                    {tCommon('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {deliveries.map((delivery) => {
                  const config = statusConfig[delivery.status] || statusConfig.PENDING;
                  const StatusIcon = config?.icon ?? Clock;

                  return (
                    <tr key={delivery.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <Link
                          to={`/trading/sales/deliveries/${delivery.id}`}
                          className="text-primary font-mono text-sm font-medium hover:underline"
                        >
                          {delivery.deliveryNumber}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/trading/sales/orders/${delivery.salesOrderId}`}
                          className="text-muted-foreground hover:text-primary font-mono text-sm"
                        >
                          {delivery.salesOrderNumber}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-sm">{delivery.customerName || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${config?.color ?? 'bg-gray-100 text-gray-700'}`}
                        >
                          <StatusIcon className="h-3 w-3" />
                          {getStatusLabel(delivery.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">{delivery.warehouseName}</td>
                      <td className="px-4 py-3">
                        {delivery.trackingNumber ? (
                          <div className="flex items-center gap-2 text-sm">
                            <MapPin className="text-muted-foreground h-4 w-4" />
                            <span className="font-mono">{delivery.trackingNumber}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">{formatDate(delivery.shipDate)}</td>
                      <td className="px-4 py-3 text-right text-sm">{delivery.itemCount}</td>
                      <td className="px-4 py-3 text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => navigate(`/trading/sales/deliveries/${delivery.id}`)}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              {tCommon('view')}
                            </DropdownMenuItem>
                            {(delivery.status === 'PENDING' ||
                              delivery.status === 'PICKED' ||
                              delivery.status === 'PACKED') && (
                              <DropdownMenuItem
                                onClick={() => markShippedMutation.mutate({ id: delivery.id })}
                              >
                                <Send className="mr-2 h-4 w-4" />
                                {t('sales.deliveries.markShipped')}
                              </DropdownMenuItem>
                            )}
                            {(delivery.status === 'SHIPPED' ||
                              delivery.status === 'IN_TRANSIT') && (
                              <DropdownMenuItem
                                onClick={() => markDeliveredMutation.mutate(delivery.id)}
                              >
                                <CheckCircle className="mr-2 h-4 w-4" />
                                {t('sales.deliveries.markDelivered')}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-sm">
                {tCommon('showing')} {(pagination.page - 1) * pagination.limit + 1} -{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} {tCommon('of')}{' '}
                {pagination.total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="border-border h-8 border px-3 text-sm disabled:opacity-50"
                >
                  {tCommon('previous')}
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === pagination.totalPages}
                  className="border-border h-8 border px-3 text-sm disabled:opacity-50"
                >
                  {tCommon('next')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
